import { memo, useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { MenuTop } from '../../../components/menu/top/menu-top';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper,
    CircularProgress,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Chip,
    Alert,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TableSortLabel,
    TextField,
    InputAdornment,
    TablePagination
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import '../styles/admin-page.css';
import '../styles/list-dd-curto.css';
import { deleteDDCurtoAnswer, getAllDDCurtoAnswer, IDDCurtoTestData } from '../../../features/search/service/search-service';
import IconButtonComponent from '../../../components/Icon/icon-button';
import { getCompaniesListSelect } from '../../../features/search/service/company-service';
import { SelectOption } from '../../../shared/types/select-options';
import { useTranslation } from '../../../shared/i18n/i18n-context';

type SortBy = 'nome' | 'record_id' | 'companyName' | 'createdAt' | 'totalTimeSeconds' | 'ed50' | 'k';
type SortDirection = 'asc' | 'desc';

const sortLabelKeys: Record<SortBy, string> = {
    nome: 'name',
    record_id: 'recordId',
    companyName: 'company',
    createdAt: 'createdAt',
    totalTimeSeconds: 'time',
    ed50: 'ED50',
    k: 'K'
};

const numericSortFields = new Set<SortBy>(['totalTimeSeconds', 'ed50', 'k']);
type SortValue = string | number | null;

const getSortValue = (test: IDDCurtoTestData, field: SortBy): SortValue => {
    if (field === 'createdAt') {
        const dateTime = test.createdAt ? new Date(test.createdAt).getTime() : NaN;
        return Number.isFinite(dateTime) ? dateTime : null;
    }

    if (numericSortFields.has(field)) {
        const value = field === 'totalTimeSeconds'
            ? Number(test.totalTimeSeconds)
            : Number.parseFloat(test[field] || '');

        return Number.isFinite(value) ? value : null;
    }

    const value = String(test[field] || '').trim().toLowerCase();
    return value || null;
};

const compareSortValues = (
    firstValue: SortValue,
    secondValue: SortValue,
    field: SortBy,
    direction: SortDirection,
    locale: string
) => {
    if (firstValue === null && secondValue === null) return 0;
    if (firstValue === null) return 1;
    if (secondValue === null) return -1;

    const comparison = numericSortFields.has(field) || field === 'createdAt'
        ? Number(firstValue) - Number(secondValue)
        : String(firstValue).localeCompare(String(secondValue), locale, { sensitivity: 'base' });

    return direction === 'asc' ? comparison : comparison * -1;
};

const formatDate = (dateString: string, locale: string, fallback: string) => {
    if (!dateString) return fallback;
    const date = new Date(dateString);
    return date.toLocaleString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const formatTime = (seconds: number) => {
    if (!seconds) return '0m 0s';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
};

interface TestsTableProps {
    tests: IDDCurtoTestData[];
    sortBy: SortBy;
    sortDirection: SortDirection;
    onSort: (column: SortBy) => void;
    onDelete: (test: IDDCurtoTestData) => void;
}

const AdminTestsTable = memo(function AdminTestsTable({
    tests,
    sortBy,
    sortDirection,
    onSort,
    onDelete
}: TestsTableProps) {
    const { language, t } = useTranslation('admin');
    const locale = language === 'en' ? 'en-US' : 'pt-BR';

    const renderSortableHeader = (column: SortBy) => (
        <TableSortLabel
            active={sortBy === column}
            direction={sortBy === column ? sortDirection : 'asc'}
            onClick={() => onSort(column)}
            className="admin-tests-sort-label"
        >
            {t(sortLabelKeys[column])}
        </TableSortLabel>
    );

    return (
        <TableContainer 
            component={Paper} 
            className="admin-tests-table-container"
            sx={{ 
                maxHeight: 'calc(100vh - 300px)',
                overflow: 'auto'
            }}
        >
            <Table size="small" stickyHeader className="admin-tests-table">
                <colgroup>
                    <col className="admin-tests-col-name" />
                    <col className="admin-tests-col-record" />
                    <col className="admin-tests-col-company" />
                    <col className="admin-tests-col-created" />
                    <col className="admin-tests-col-time" />
                    <col className="admin-tests-col-ed50" />
                    <col className="admin-tests-col-k" />
                    <col className="admin-tests-col-actions" />
                </colgroup>
                <TableHead>
                    <TableRow>
                        <TableCell className="admin-tests-table-cell-header">
                            {renderSortableHeader('nome')}
                        </TableCell>
                        <TableCell className="admin-tests-table-cell-header">
                            {renderSortableHeader('record_id')}
                        </TableCell>
                        <TableCell className="admin-tests-table-cell-header">
                            {renderSortableHeader('companyName')}
                        </TableCell>
                        <TableCell className="admin-tests-table-cell-header">
                            {renderSortableHeader('createdAt')}
                        </TableCell>
                        <TableCell className="admin-tests-table-cell-header">
                            {renderSortableHeader('totalTimeSeconds')}
                        </TableCell>
                        <TableCell className="admin-tests-table-cell-header">
                            {renderSortableHeader('ed50')}
                        </TableCell>
                        <TableCell className="admin-tests-table-cell-header">
                            {renderSortableHeader('k')}
                        </TableCell>
                        <TableCell 
                            align="center"
                            className="admin-tests-table-cell-header"
                            sx={{ width: '70px' }}
                        >
                            {t('actions')}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tests.map((test) => (
                        <TableRow 
                            key={test.id}
                            className="admin-tests-table-row"
                        >
                            <TableCell className="admin-tests-table-cell-name" title={test.nome || t('notAvailable')}>
                                {test.nome || t('notAvailable')}
                            </TableCell>
                            <TableCell className="admin-tests-table-cell-body">
                                {test.record_id || t('notAvailable')}
                            </TableCell>
                            <TableCell className="admin-tests-table-cell-chip">
                                <Chip 
                                    label={test.companyName || t('notAvailable')}
                                    size="small"
                                    sx={{ 
                                        backgroundColor: '#e8f5e9',
                                        color: '#2e7d32',
                                        height: '22px',
                                        fontSize: '11px',
                                        fontFamily: 'Roboto, sans-serif',
                                        fontWeight: 500
                                    }}
                                />
                            </TableCell>
                            <TableCell className="admin-tests-table-cell-body">
                                {formatDate(test.createdAt || '', locale, t('notAvailable'))}
                            </TableCell>
                            <TableCell className="admin-tests-table-cell-body">
                                {formatTime(test.totalTimeSeconds || 0)}
                            </TableCell>
                            <TableCell className="admin-tests-table-cell-body">
                                {test.ed50 ? parseFloat(test.ed50).toFixed(2) : t('notAvailable')}
                            </TableCell>
                            <TableCell className="admin-tests-table-cell-body">
                                {test.k ? parseFloat(test.k).toFixed(4) : t('notAvailable')}
                            </TableCell>
                            <TableCell align="center" className="admin-tests-table-cell-actions">
                                <IconButtonComponent 
                                    label=""
                                    icon={<DeleteIcon />}
                                    onClick={() => onDelete(test)}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                    {Array.from({ length: Math.max(0, 25 - tests.length) }).map((_, index) => (
                        <TableRow
                            key={`empty-row-${index}`}
                            className="admin-tests-table-row admin-tests-table-row--empty"
                        >
                            <TableCell colSpan={8}>&nbsp;</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
});

export const AdminDDCurtoTestsPage = () => {
    const { language, t } = useTranslation('admin');
    const locale = language === 'en' ? 'en-US' : 'pt-BR';
    const [tests, setTests] = useState<IDDCurtoTestData[]>([]);
    const [companies, setCompanies] = useState<SelectOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedTest, setSelectedTest] = useState<IDDCurtoTestData | null>(null);
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [companyFilter, setCompanyFilter] = useState<string>('');
    const [sortBy, setSortBy] = useState<SortBy>('createdAt');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [filtersLoading, setFiltersLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const deferredSearchTerm = useDeferredValue(searchTerm);
    const isSearchDeferred = searchTerm !== deferredSearchTerm;

    const searchableTests = useMemo(() => {
        return tests.map((test) => ({
            test,
            searchableText: [
                test.nome,
                test.record_id,
                test.companyName
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()
        }));
    }, [tests]);

    useEffect(() => {
        fetchAllTests();
    }, []);

    useEffect(() => {
        setPage(0);
    }, [companyFilter, deferredSearchTerm, sortBy, sortDirection]);

    useEffect(() => {
        if (loading) {
            return;
        }

        setFiltersLoading(true);
        const timeoutId = window.setTimeout(() => setFiltersLoading(false), 220);

        return () => window.clearTimeout(timeoutId);
    }, [companyFilter, deferredSearchTerm, loading, searchTerm, sortBy, sortDirection]);

    const filteredTests = useMemo(() => {
        const term = deferredSearchTerm.toLowerCase().trim();
        const filtered = searchableTests
            .filter(({ test, searchableText }) => {
            const matchesCompany = !companyFilter || test.companyId === companyFilter;

            if (!matchesCompany) {
                return false;
            }

            if (!term) {
                return true;
            }

            return searchableText.includes(term);
        })
            .map(({ test }) => test);

            const sorted = [...filtered].sort((firstTest, secondTest) => {
            const firstValue = getSortValue(firstTest, sortBy);
            const secondValue = getSortValue(secondTest, sortBy);
            return compareSortValues(firstValue, secondValue, sortBy, sortDirection, locale);
        });

        return sorted;
    }, [companyFilter, deferredSearchTerm, locale, searchableTests, sortBy, sortDirection]);

    const paginatedTests = useMemo(() => {
        const firstRowIndex = page * rowsPerPage;
        return filteredTests.slice(firstRowIndex, firstRowIndex + rowsPerPage);
    }, [filteredTests, page, rowsPerPage]);

    const fetchAllTests = async () => {
        try {
            setLoading(true);
            const [response, companiesResponse] = await Promise.all([
                getAllDDCurtoAnswer(),
                getCompaniesListSelect()
            ]);

            setTests(response.data);
            setCompanies(companiesResponse);
        } catch (error) {
            console.error('Erro ao carregar testes:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
    };

    const handleChangePage = (_event: unknown, nextPage: number) => {
        setPage(nextPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextRowsPerPage = Number.parseInt(event.target.value, 10);

        setRowsPerPage(nextRowsPerPage);
        setPage(0);
    };

    const handleCompanyFilterChange = (event: SelectChangeEvent) => {
        setCompanyFilter(event.target.value);
    };

    const handleSort = useCallback((column: SortBy) => {
        if (sortBy === column) {
            setSortDirection((currentDirection) => currentDirection === 'asc' ? 'desc' : 'asc');
            return;
        }

        setSortBy(column);
        setSortDirection('asc');
    }, [sortBy]);

    const handleDeleteClick = useCallback((test: IDDCurtoTestData) => {
        setSelectedTest(test);
        setDeleteDialogOpen(true);
    }, []);

    const handleDeleteConfirm = async () => {
        if (!selectedTest) return;

        try {
            await deleteDDCurtoAnswer(selectedTest.id);
            setSuccessMessage(t('testDeletedSuccess').replace('{name}', selectedTest.nome || t('notAvailable')));
            setDeleteDialogOpen(false);
            setSelectedTest(null);
            
            fetchAllTests();
            
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (error) {
            console.error('Erro ao excluir teste:', error);
            alert(t('testDeleteError'));
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setSelectedTest(null);
    };

    const hasActiveFilters = Boolean(deferredSearchTerm.trim() || companyFilter);
    const showLoadingOverlay = loading || filtersLoading || isSearchDeferred;

    return (
        <div>
            <MenuTop />
            
            {/* Chip Fixo no Canto Direito */}
            <Chip 
                label={`${t('totalPrefix')} ${filteredTests.length}`}
                className="admin-tests-chip-fixed"
                sx={{ 
                    backgroundColor: '#3a8d8e', 
                    color: 'white',
                    fontWeight: 500,
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    padding: '20px 12px'
                }}
            />

            <div className="admin-page-container admin-tests-container">
                {/* Header Clean */}
                <div className="admin-tests-header">
                    <h1 className="admin-tests-title">
                        {t('appliedTests')}
                    </h1>

                    <div className="admin-tests-filters-container">
                        <div className="admin-tests-search-wrapper">
                            <TextField
                                id="search-tests"
                                className="admin-tests-filter-field"
                                label={t('searchTests')}
                                value={searchTerm}
                                onChange={handleSearchChange}
                                type="text"
                                size="small"
                                fullWidth
                                autoComplete="off"
                                InputProps={{
                                    endAdornment: searchTerm ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={handleClearSearch}
                                                edge="end"
                                                title={t('clearSearch')}
                                                aria-label={t('clearSearch')}
                                                className="admin-tests-clear-search-button"
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null
                                }}
                            />
                        </div>
                        <FormControl className="admin-tests-filter-field admin-tests-company-filter" size="small">
                            <InputLabel id="admin-tests-company-filter-label">{t('company')}</InputLabel>
                            <Select
                                labelId="admin-tests-company-filter-label"
                                value={companyFilter}
                                label={t('company')}
                                onChange={handleCompanyFilterChange}
                            >
                                <MenuItem value="">{t('allCompanies')}</MenuItem>
                                {companies.map((company) => (
                                    <MenuItem key={company.id} value={String(company.id)}>
                                        {company.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                </div>

                {successMessage && (
                    <Alert 
                        severity="success" 
                        onClose={() => setSuccessMessage('')}
                        className="admin-tests-alert"
                    >
                        {successMessage}
                    </Alert>
                )}

                <div className="admin-tests-results">
                    {showLoadingOverlay && (
                        <div className="admin-tests-loading-overlay">
                            <div className="admin-tests-loading-panel">
                                <CircularProgress 
                                    size={36} 
                                    thickness={4}
                                    sx={{ color: '#3a8d8e' }} 
                                />
                                <Typography className="admin-tests-loading-text">
                                    {t('loadingTests')}
                                </Typography>
                            </div>
                        </div>
                    )}

                    {!loading && (
                    <>
                        {hasActiveFilters && filteredTests.length === 0 && (
                            <Alert severity="info" className="admin-tests-alert">
                                {t('noTestsForFilters')}
                            </Alert>
                        )}

                        {!hasActiveFilters && filteredTests.length === 0 && (
                            <div className="admin-tests-empty-state">
                                <HourglassEmptyIcon className="admin-tests-empty-state-icon" />
                                <Typography className="admin-tests-empty-state-text">
                                    {t('noTestsFound')}
                                </Typography>
                                <Typography className="admin-tests-empty-state-subtext">
                                    {t('testsWillAppear')}
                                </Typography>
                            </div>
                        )}

                        {filteredTests.length > 0 && (
                            <>
                                <AdminTestsTable
                                    tests={paginatedTests}
                                    sortBy={sortBy}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                    onDelete={handleDeleteClick}
                                />
                                <TablePagination
                                    component="div"
                                    className="admin-tests-pagination"
                                    count={filteredTests.length}
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    rowsPerPageOptions={[25, 50, 100]}
                                    labelRowsPerPage={t('rowsPerPage')}
                                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${language === 'en' ? 'of' : 'de'} ${count}`}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </>
                        )}
                    </>
                    )}
                </div>
            </div>

            {/* Dialog de Confirmação */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ 
                    backgroundColor: '#f44336', 
                    color: 'white', 
                    padding: '16px 24px',
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 500,
                    fontSize: '18px'
                }}>
                    {t('confirmDelete')}
                </DialogTitle>
                <DialogContent sx={{ padding: '24px' }}>
                    <Typography 
                        sx={{ 
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: '14px',
                            color: '#666',
                            marginBottom: '16px'
                        }}
                    >
                        {t('confirmDeleteTestQuestion')}
                    </Typography>
                    {selectedTest && (
                        <div className="admin-tests-dialog-info">
                            <Typography variant="body2" className="admin-tests-dialog-info-text">
                                <strong>{t('name')}:</strong> {selectedTest.nome}
                            </Typography>
                            <Typography variant="body2" className="admin-tests-dialog-info-text">
                                <strong>Record ID:</strong> {selectedTest.record_id}
                            </Typography>
                            <Typography variant="body2" className="admin-tests-dialog-info-text">
                                <strong>{t('company')}:</strong> {selectedTest.companyName}
                            </Typography>
                            <Typography variant="body2" className="admin-tests-dialog-info-text">
                                <strong>{t('createdAt')}:</strong> {formatDate(selectedTest.createdAt || '', locale, t('notAvailable'))}
                            </Typography>
                        </div>
                    )}
                    <Typography variant="body2" className="admin-tests-dialog-warning">
                        {t('irreversibleAction')}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: '16px 24px' }}>
                    <Button 
                        onClick={handleDeleteCancel}
                        sx={{ 
                            color: '#666',
                            fontFamily: 'Roboto, sans-serif',
                            textTransform: 'none',
                            fontSize: '14px'
                        }}
                    >
                        {t('cancel')}
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        sx={{ 
                            backgroundColor: '#f44336',
                            fontFamily: 'Roboto, sans-serif',
                            textTransform: 'none',
                            fontSize: '14px',
                            '&:hover': { backgroundColor: '#d32f2f' }
                        }}
                    >
                        {t('delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};
